import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import {
  FaHome,
  FaStar,
  FaCalendarAlt,
  FaUtensils,
  FaImages,
  FaTrash,
  FaPlus,
  FaSave,
  FaCloudUploadAlt,
  FaCheckCircle,
  FaYoutube
} from "react-icons/fa";
import "../styles/ManageHome.css";

const ManageHome = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  /* ================= STATES ================= */
  const [heroTitle, setHeroTitle] = useState("");
  const [heroSubtitle, setHeroSubtitle] = useState("");
  const [heroImage, setHeroImage] = useState("");

  const [highlights, setHighlights] = useState([]);
  const [events, setEvents] = useState([]);
  const [annadanam, setAnnadanam] = useState([]);
  const [gallery, setGallery] = useState([]);

  // UI Redesign States
  const [activeTab, setActiveTab] = useState("hero"); // "hero" | "highlights" | "events" | "annadanam" | "gallery"

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    fetchHomeData();
  }, []);

  const fetchHomeData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_ENDPOINTS.HOME);
      const data = res.data.data || res.data;

      setHeroTitle(data.heroTitle || "");
      setHeroSubtitle(data.heroSubtitle || "");
      setHeroImage(data.heroImage || "");
      setHighlights(data.highlights || []);
      setEvents(data.events || []);
      setAnnadanam(data.annadanam || []);
      setGallery(data.gallery || []);
    } catch (err) {
      console.log("LOAD ERROR:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= IMAGE COMPRESS ================= */
  const resizeImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const MAX_WIDTH = 800;
          const scaleSize = MAX_WIDTH / img.width;

          canvas.width = MAX_WIDTH;
          canvas.height = img.height * scaleSize;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          const compressedBase64 = canvas.toDataURL("image/jpeg", 0.6);
          resolve(compressedBase64);
        };
      };
    });
  };

  /* ================= HERO IMAGE ================= */
  const handleHeroImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const compressed = await resizeImage(file);
      setHeroImage(compressed);
    } catch (error) {
      console.log(error);
    }
  };

  /* ================= HIGHLIGHTS ================= */
  const updateHighlight = (i, field, value) => {
    setHighlights((prev) => {
      const updated = [...prev];
      updated[i][field] = value;
      return updated;
    });
  };

  const addHighlight = () => {
    setHighlights((prev) => [
      ...prev,
      { title: "", desc: "" }
    ]);
  };

  const deleteHighlight = (i) => {
    if (window.confirm("Are you sure you want to delete this highlight?")) {
      setHighlights((prev) => prev.filter((_, index) => index !== i));
    }
  };

  /* ================= EVENTS ================= */
  const getEmbedUrl = (url) => {
    if (!url) return "";
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : url;
  };

  const updateEvent = (i, field, value) => {
    setEvents((prev) => {
      const updated = [...prev];
      if (field === "youtube") {
        updated[i].youtube = value;
        updated[i].embed = getEmbedUrl(value);
      } else {
        updated[i][field] = value;
      }
      return updated;
    });
  };

  const addEvent = () => {
    setEvents((prev) => [
      ...prev,
      { title: "", desc: "", youtube: "", embed: "" }
    ]);
  };

  const deleteEvent = (i) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      setEvents((prev) => prev.filter((_, index) => index !== i));
    }
  };

  /* ================= ANNADANAM ================= */
  const updateAnnadanam = (i, field, value) => {
    setAnnadanam((prev) => {
      const updated = [...prev];
      updated[i][field] = value;
      return updated;
    });
  };

  const addAnnadanam = () => {
    setAnnadanam((prev) => [
      ...prev,
      { title: "", desc: "" }
    ]);
  };

  const deleteAnnadanam = (i) => {
    if (window.confirm("Are you sure you want to delete this Annadanam item?")) {
      setAnnadanam((prev) => prev.filter((_, index) => index !== i));
    }
  };

  /* ================= GALLERY ================= */
  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 100) {
      alert("Maximum 100 images at once");
      return;
    }

    try {
      setUploading(true);
      const compressedImages = await Promise.all(
        files.map((file) => resizeImage(file))
      );
      setGallery((prev) => [...prev, ...compressedImages]);
    } catch (error) {
      console.log(error);
    } finally {
      setUploading(false);
    }
  };

  const deleteGallery = (i) => {
    if (window.confirm("Are you sure you want to delete this image?")) {
      setGallery((prev) => prev.filter((_, index) => index !== i));
    }
  };

  /* ================= SAVE ================= */
  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);

      const payload = {
        heroTitle,
        heroSubtitle,
        heroImage,
        highlights,
        events,
        annadanam,
        gallery
      };

      await axios.put(API_ENDPOINTS.HOME, payload);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      alert("🎉 Home Page Saved Successfully!");
    } catch (err) {
      console.log("SAVE ERROR:", err);
      alert(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  /* ================= LOADING ================= */
  if (loading) {
    return (
      <div className="home-loading">
        <div className="spinner"></div>
        <h2>Loading Home Configuration...</h2>
        <p>Connecting to backend server</p>
      </div>
    );
  }

  return (
    <div className="manage-home-container">
      {/* BANNER SECTION */}
      <div className="home-banner">
        <div className="banner-content">
          <h1>🏛️ Manage Home Page</h1>
          <p>Update the hero image, highlights, events, and gallery sections for the public website homepage.</p>
        </div>
        <div className="banner-stats">
          <div className="stat-pill">
            <FaStar className="icon" />
            <span><strong>{highlights.length}</strong> Highlights</span>
          </div>
          <div className="stat-pill">
            <FaCalendarAlt className="icon" />
            <span><strong>{events.length}</strong> Events</span>
          </div>
          <div className="stat-pill">
            <FaImages className="icon" />
            <span><strong>{gallery.length}</strong> Gallery Images</span>
          </div>
        </div>
      </div>

      {/* DASHBOARD NAVIGATION & CONTROLS */}
      <div className="dashboard-controls">
        <div className="tabs-wrapper">
          <button 
            className={`tab-btn ${activeTab === "hero" ? "active" : ""}`}
            onClick={() => setActiveTab("hero")}
          >
            <FaHome /> Hero Banner
          </button>
          <button 
            className={`tab-btn ${activeTab === "highlights" ? "active" : ""}`}
            onClick={() => setActiveTab("highlights")}
          >
            <FaStar /> Highlights
          </button>
          <button 
            className={`tab-btn ${activeTab === "events" ? "active" : ""}`}
            onClick={() => setActiveTab("events")}
          >
            <FaCalendarAlt /> Events
          </button>
          <button 
            className={`tab-btn ${activeTab === "annadanam" ? "active" : ""}`}
            onClick={() => setActiveTab("annadanam")}
          >
            <FaUtensils /> Annadanam
          </button>
          <button 
            className={`tab-btn ${activeTab === "gallery" ? "active" : ""}`}
            onClick={() => setActiveTab("gallery")}
          >
            <FaImages /> Home Gallery
          </button>
        </div>

        <div className="quick-action-buttons">
          {activeTab === "highlights" && (
            <button className="action-btn add-btn-primary" onClick={addHighlight}>
              <FaPlus /> Add Highlight
            </button>
          )}
          {activeTab === "events" && (
            <button className="action-btn add-btn-primary" onClick={addEvent}>
              <FaPlus /> Add Event
            </button>
          )}
          {activeTab === "annadanam" && (
            <button className="action-btn add-btn-primary" onClick={addAnnadanam}>
              <FaPlus /> Add Annadanam
            </button>
          )}
          <button 
            className={`action-btn save-btn-primary ${saving ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="spinner-small"></div> Saving...
              </>
            ) : saveSuccess ? (
              <>
                <FaCheckCircle /> Saved!
              </>
            ) : (
              <>
                <FaSave /> Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* PANEL CONTAINER */}
      <div className="panel-container">
        
        {/* HERO SECTION PANEL */}
        {activeTab === "hero" && (
          <div className="panel-card fade-in">
            <div className="panel-card-header">
              <h3><FaHome className="title-icon" /> Hero Banner Settings</h3>
              <p>Customize the primary title, subtitle, and full-bleed banner image of the website's home page.</p>
            </div>
            
            <div className="panel-card-body">
              <div className="form-group">
                <label>Hero Title / Main Greeting</label>
                <input
                  type="text"
                  value={heroTitle}
                  onChange={(e) => setHeroTitle(e.target.value)}
                  placeholder="e.g. Welcome to Gnana Sai Baba Mandir"
                />
              </div>

              <div className="form-group">
                <label>Hero Subtitle / Description</label>
                <textarea
                  value={heroSubtitle}
                  onChange={(e) => setHeroSubtitle(e.target.value)}
                  placeholder="e.g. Experience peace, divinity, and Sai Baba's spiritual wisdom..."
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Hero Banner Image</label>
                <div className="image-upload-wrapper">
                  <label className="custom-image-uploader">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleHeroImage}
                    />
                    <FaCloudUploadAlt className="upload-icon" />
                    <span>Upload New Banner Image</span>
                  </label>
                  
                  {heroImage && (
                    <div className="hero-preview-box">
                      <img src={heroImage} alt="Hero Banner Preview" className="hero-preview-img" />
                      <div className="preview-label">Live Banner Image</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* HIGHLIGHTS PANEL */}
        {activeTab === "highlights" && (
          <div className="directory-section fade-in">
            {highlights.length === 0 ? (
              <div className="empty-state">
                <FaStar className="empty-icon" />
                <h3>No Highlights Added</h3>
                <p>Add highlights to showcase the primary features of the temple, e.g. teachings, daily rituals, or activities.</p>
                <button className="action-btn add-btn-primary" onClick={addHighlight}>
                  <FaPlus /> Add Highlight Now
                </button>
              </div>
            ) : (
              <div className="cards-grid">
                {highlights.map((item, i) => (
                  <div key={i} className="content-card">
                    <div className="card-header-actions">
                      <span className="card-number">Highlight #{i + 1}</span>
                      <button className="card-delete-btn" onClick={() => deleteHighlight(i)}>
                        <FaTrash /> Delete
                      </button>
                    </div>

                    <div className="card-body">
                      <div className="form-group-compact">
                        <label>Highlight Title</label>
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) => updateHighlight(i, "title", e.target.value)}
                          placeholder="e.g. Daily Aarti & Worship"
                        />
                      </div>
                      <div className="form-group-compact">
                        <label>Description</label>
                        <textarea
                          value={item.desc}
                          onChange={(e) => updateHighlight(i, "desc", e.target.value)}
                          placeholder="Provide details about this feature..."
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* EVENTS PANEL */}
        {activeTab === "events" && (
          <div className="directory-section fade-in">
            {events.length === 0 ? (
              <div className="empty-state">
                <FaCalendarAlt className="empty-icon" />
                <h3>No Events Added</h3>
                <p>Add events to let visitors know about upcoming festivals, special poojas, or functions.</p>
                <button className="action-btn add-btn-primary" onClick={addEvent}>
                  <FaPlus /> Add Event Now
                </button>
              </div>
            ) : (
              <div className="cards-grid">
                {events.map((item, i) => (
                  <div key={i} className="content-card">
                    <div className="card-header-actions">
                      <span className="card-number">Event #{i + 1}</span>
                      <button className="card-delete-btn" onClick={() => deleteEvent(i)}>
                        <FaTrash /> Delete
                      </button>
                    </div>

                    <div className="card-body">
                      <div className="form-group-compact">
                        <label>Event Title</label>
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) => updateEvent(i, "title", e.target.value)}
                          placeholder="e.g. Guru Purnima Celebrations"
                        />
                      </div>

                      <div className="form-group-compact">
                        <label>Description</label>
                        <textarea
                          value={item.desc}
                          onChange={(e) => updateEvent(i, "desc", e.target.value)}
                          placeholder="Enter details, timings, and location of the event..."
                          rows={3}
                        />
                      </div>

                      <div className="form-group-compact">
                        <label><FaYoutube style={{ color: "#ef4444", marginRight: "6px" }} /> YouTube Video Link (Optional)</label>
                        <input
                          type="text"
                          value={item.youtube}
                          onChange={(e) => updateEvent(i, "youtube", e.target.value)}
                          placeholder="e.g. https://www.youtube.com/watch?v=..."
                        />
                      </div>

                      {item.embed && (
                        <div className="video-preview-wrapper">
                          <iframe
                            src={item.embed}
                            title={item.title}
                            allowFullScreen
                            className="video-frame-preview"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ANNADANAM PANEL */}
        {activeTab === "annadanam" && (
          <div className="directory-section fade-in">
            {annadanam.length === 0 ? (
              <div className="empty-state">
                <FaUtensils className="empty-icon" />
                <h3>No Annadanam Items Added</h3>
                <p>List details about free food services, distribution timings, or donation calls.</p>
                <button className="action-btn add-btn-primary" onClick={addAnnadanam}>
                  <FaPlus /> Add Annadanam Now
                </button>
              </div>
            ) : (
              <div className="cards-grid">
                {annadanam.map((item, i) => (
                  <div key={i} className="content-card">
                    <div className="card-header-actions">
                      <span className="card-number">Annadanam Entry #{i + 1}</span>
                      <button className="card-delete-btn" onClick={() => deleteAnnadanam(i)}>
                        <FaTrash /> Delete
                      </button>
                    </div>

                    <div className="card-body">
                      <div className="form-group-compact">
                        <label>Annadanam Title / Session</label>
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) => updateAnnadanam(i, "title", e.target.value)}
                          placeholder="e.g. Daily Afternoon Prasadam"
                        />
                      </div>

                      <div className="form-group-compact">
                        <label>Description / Distribution Info</label>
                        <textarea
                          value={item.desc}
                          onChange={(e) => updateAnnadanam(i, "desc", e.target.value)}
                          placeholder="Provide details about timings, distribution locations, and sponsorships..."
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* GALLERY PANEL */}
        {activeTab === "gallery" && (
          <div className="panel-card fade-in">
            <div className="panel-card-header">
              <h3><FaImages className="title-icon" /> Home Gallery Carousel</h3>
              <p>Upload the showcase images that rotate on the homepage slider. Max 100 images at once.</p>
            </div>

            <div className="panel-card-body">
              <div className="upload-drop-zone">
                <label className="upload-drag-label">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryUpload}
                  />
                  <FaCloudUploadAlt className="drag-icon" />
                  <h4>Select or Drag Images Here</h4>
                  <p>Support PNG, JPEG formats. Multi-selection enabled.</p>
                </label>
              </div>

              {uploading && (
                <div className="upload-progress">
                  <div className="spinner-small"></div>
                  <span>Compressing and uploading images...</span>
                </div>
              )}

              <div className="gallery-layout-grid">
                {gallery.map((img, i) => (
                  <div key={i} className="gallery-thumbnail-card">
                    <img
                      src={img}
                      alt={`Home Gallery ${i}`}
                      loading="lazy"
                      className="gallery-thumbnail-img"
                    />
                    <div className="gallery-img-overlay">
                      <button className="gallery-delete-icon-btn" onClick={() => deleteGallery(i)}>
                        <FaTrash />
                      </button>
                    </div>
                    <div className="gallery-img-number">#{i + 1}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* FLOATING ACTION BAR FOR SAVE */}
      <div className="floating-save-bar">
        <div className="save-bar-info">
          <span>Unsaved homepage configuration changes will be lost unless saved.</span>
        </div>
        <button 
          className={`action-btn save-btn-primary ${saving ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <div className="spinner-small"></div> Saving changes...
            </>
          ) : saveSuccess ? (
            <>
              <FaCheckCircle /> Saved Successfully!
            </>
          ) : (
            <>
              <FaSave /> Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ManageHome;
